import { Component, Inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

export interface SettingsDialogData {
  title: string;
  defaultValue: string;
}

@Component({
  selector: 'app-settings-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatCardModule,
    MatTooltipModule,
    MatIconModule,
    MatDialogModule,
    MatButtonModule
  ],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
    <hr/>
      <mat-form-field style="margin-top:5px" appearance="outline" class="full-width">
        <mat-label>Default Value</mat-label>
        <input matInput [(ngModel)]="data.defaultValue" placeholder="Enter default value">
        <mat-icon 
          matSuffix 
          matTooltip="This default value will be applied when the selected field is empty" 
          color="primary">
          info
        </mat-icon>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-raised-button color="primary" (click)="onSave()">Save</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .full-width {
      width: 100%;
    }
    mat-dialog-content {
      min-height: 100px;
      min-width: 300px;
    }
    mat-hint {
      font-style: italic;
    }

    // Dark Theme Support
    :host-context(body.dark-theme) {
      h2[mat-dialog-title] {
        color: var(--form-text-primary, #ffffff);
      }

      mat-dialog-content {
        color: var(--form-text-primary, #ffffff);

        hr {
          border-color: var(--form-border-color, #4a5568);
        }

        ::ng-deep {
          .mat-mdc-form-field {
            .mdc-text-field {
              background-color: var(--form-input-bg, #2d3748);
            }

            .mdc-notched-outline__leading,
            .mdc-notched-outline__notch,
            .mdc-notched-outline__trailing {
              border-color: var(--form-input-border, #4a5568);
            }

            &.mat-focused {
              .mdc-notched-outline__leading,
              .mdc-notched-outline__notch,
              .mdc-notched-outline__trailing {
                border-color: var(--form-primary-color, #00a1ff);
              }
            }

            .mat-mdc-form-field-label,
            .mdc-floating-label,
            input {
              color: var(--form-text-primary, #ffffff);
            }

            mat-icon {
              color: var(--form-primary-color, #00a1ff);
            }
          }
        }
      }

      mat-dialog-actions {
        button {
          &.mat-mdc-button {
            color: var(--form-text-secondary, #b0b0b0);

            &:hover {
              background-color: var(--form-cancel-hover-bg, #3a3a3a);
            }
          }

          &.mat-mdc-raised-button {
            &[color="primary"] {
              background-color: var(--form-primary-color, #00a1ff);
              color: var(--form-text-on-primary, #ffffff);

              &:hover {
                background-color: var(--form-primary-hover, #0086d9);
              }
            }
          }
        }
      }
    }
  `]
})
export class SettingsDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<SettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SettingsDialogData
  ) {}

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.dialogRef.close(this.data);
  }
}